import Modifier from 'ember-modifier'
import type { ArgsFor } from 'ember-modifier'
import type RouterService from '@ember/routing/router-service'
import { assert } from '@ember/debug'
import { service } from '@ember/service'
import { registerDestructor } from '@ember/destroyable'
import type Owner from '@ember/owner'
import type Transition from '@ember/routing/transition'

/**
 * Reset scroll to top for window
 */
export function resetOutletScroll() {
  document.querySelector('#overflow-outlet')?.scrollTo(0, 0)
}

function routeWillChange(transition: Transition) {
  // only reset scroll if not a QP-only transition. transition.queryParamsOnly is private
  if (transition.from?.name !== transition.to?.name) resetOutletScroll()
}

interface Signature {
  Args: {
    Named: {
      onClick?: boolean
      onRouterChange?: boolean
      whenValueChanges?: boolean
      [named: string]: unknown
    }
    Positional: unknown[]
  }
  Element: HTMLElement
}

/**
 * Modifier used to reset scroll to top
 *
 * Can be used in multiple ways
 *
 * `onSetup=true`                  will reset to top when element is rendered
 * `onClick=true`                  will reset to top when element is clicked
 * `onRouterChange=true`           will reset whenever the router willTransition event is fired
 * `whenValueChanges=someProperty` will reset whenever the value of the given property changes
 *
 */
export class ResetScroll extends Modifier<Signature> {
  @service router!: RouterService

  didSetup = false

  onRouterChange = false

  constructor(owner: Owner, args: ArgsFor<Signature>) {
    super(owner, args)
    assert('The reset-scroll modifier does not accept positional parameters', args.positional.length === 0)

    assert(
      'The reset-scroll modifier requires at least on named parameter from onClick, onRouterChange or whenValueChanges',
      args.named.onClick || args.named.onRouterChange || args.named.whenValueChanges !== undefined,
    )

    registerDestructor(this, this.cleanup)
  }

  modify(
    element: Signature['Element'],
    _positional: Signature['Args']['Positional'],
    named: Signature['Args']['Named'],
  ) {
    if (!this.didSetup) {
      if (named.onSetup) resetOutletScroll()
      if (named.onClick) {
        element.addEventListener('click', resetOutletScroll)
      }
      if (named.onRouterChange) {
        this.onRouterChange = true
        this.router.on('routeWillChange', routeWillChange)
      }
      this.didSetup = true
    }

    if (named.whenValueChanges !== undefined) {
      resetOutletScroll()
    }
  }

  cleanup = () => {
    if (this.onRouterChange) {
      // Note this errors if you attempt to unbind the callback and it wasn't bound
      this.router.off('routeWillChange', routeWillChange)
    }
  }
}

export default ResetScroll
